import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import { useCallback } from 'react'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(14)};
  inset-block-end: ${rem(14)};
  inset-inline-start: ${rem(20)};
  position: absolute;
  z-index: 1;

  ${mediaQueryUp.tablet} {
    inset-block-end: ${rem(30)};
    inset-inline-start: ${rem(30)};
  }
`

const CloseButtonContainer = styled.div`
  inset-block-end: ${rem(14)};
  inset-inline-end: ${rem(20)};
  position: absolute;
  z-index: 1;

  ${mediaQueryUp.tablet} {
    inset-block-end: ${rem(30)};
    inset-inline-end: ${rem(30)};
  }
`

const Banner = styled.div`
  height: ${rem(175)};
  isolation: isolate;
  margin-inline: auto;
  margin-block-end: ${rem(48)};
  max-width: ${rem(1140)};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(285)};
  }

  &[data-variant='modal'] {
    margin-block-end: 0;
    margin-inline: 0;
    max-width: none;
    width: 100%;
  }
`

const BackgroundImage = styled(Image)`
  inset: 0;
  position: absolute;
  width: 100%;
  z-index: -1;
`

const BannerContent = styled.div`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  inset-block-start: ${rem(20)};
  inset-inline-start: ${rem(20)};
  max-width: ${rem(220)};
  position: absolute;
  z-index: 1;

  ${mediaQueryUp.tablet} {
    inset-block-start: ${rem(30)};
    inset-inline-start: ${rem(30)};
    max-width: ${rem(342)};
  }

  ${Banner}[data-size='medium'] & {
    inset-block-start: ${rem(20)};
    inset-inline-start: ${rem(20)};
    max-width: ${rem(181)};
  }
`

const BannerHeading = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-size: ${rem(34)} !important;
  line-height: 1.25;

  ${mediaQueryUp.tablet} {
    font-size: ${rem(48)} !important;
  }

  ${Banner}[data-size='medium'] & {
    font-size: ${rem(18.959)} !important;
    line-height: 1.15;
  }
`

const BannerDescription = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-size: ${rem(20)} !important;
  letter-spacing: ${rem(-0.34)};
  line-height: 1.15;
  font-weight: lighter;
  margin-top: ${rem(13)};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(28)} !important;
    letter-spacing: ${rem(-0.52)};
    margin-top: ${rem(19)};
  }

  ${Banner}[data-size='medium'] & {
    font-size: ${rem(12.347)} !important;
    letter-spacing: ${rem(-0.28)};
    margin-top: ${rem(11)};
  }
`

const StyledTextButton = styled(TextButton)`
  && {
    font-size: ${rem(18)};
  }

  ${Banner}[data-size='medium'] & {
    && {
      font-size: ${rem(12)};
    }
  }
`

const LegalText = styled(BodyTextBlock)`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-size: ${rem(10)} !important;
  line-height: 1.2;
  width: ${rem(168)};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(12)} !important;
    width: ${rem(250)};
  }

  ${Banner}[data-size='medium'] & {
    font-size: ${rem(8)} !important;
    width: ${rem(134)};
  }
`

interface Props {
  variant?: 'default' | 'modal'
  shouldShowCloseButton?: boolean
  onFindOutMoreClick?: () => void
  onCloseClick?: () => void
}

export default function AgentHubEarnYourOwnTourBanner({
  variant = 'default',
  shouldShowCloseButton = false,
  onFindOutMoreClick,
  onCloseClick,
}: Props) {
  const handleFindOutMoreClick = useCallback(() => {
    onFindOutMoreClick?.()
  }, [onFindOutMoreClick])

  const handleCloseClick = useCallback(() => {
    onCloseClick?.()
  }, [onCloseClick])

  if (!isAgentHubEnabled) {
    return null
  }

  return (
    <Banner data-variant={variant}>
      <BackgroundImage alt="" fit="center" id="mdi1za9qupjvbmic0zbi" objectFit="cover" quality="best" />
      <BannerContent>
        <BannerHeading variant="large">
          <Heading as="h2" variant="heading2">
            <FormattedMessage
              id="nHRoe5fikGuW41eRwEXU"
              defaultMessage="Book Tours.<lineBreak></lineBreak><i>Earn</i> Your Own."
              
              values={{
                i: chunks => <i>{chunks}</i>,
                lineBreak: () => <br />,
              }}
            />
          </Heading>
        </BannerHeading>
        <BannerDescription variant="large">
          <FormattedMessage
            id="LB4cZAK2ZPF-7dAeXGy3"
            defaultMessage="Sell 10 spots and earn your own complimentary tour."
            
          />
        </BannerDescription>
      </BannerContent>
      <ButtonContainer>
        <StyledTextButton
          endIcon={<LineAngleRightBIcon />}
          kind="primary"
          size="large"
          to="/campaign/tours"
          variant="ghost"
          target="_blank"
          onClick={onFindOutMoreClick ? handleFindOutMoreClick : undefined}
        >
          <FormattedMessage
            id="tA3k60FjuWtehEJ-2pXA"
            defaultMessage="Find out more"
            
          />
        </StyledTextButton>
        <LegalText variant="small">
          <FormattedMessage
            id="F1gD-fz7ieZrFqRj1eWt"
            defaultMessage="T&Cs apply.<lineBreak></lineBreak>Offer available on Luxury Escapes Tours only."
            
            values={{ lineBreak: () => <br /> }}
          />
        </LegalText>
      </ButtonContainer>
      {shouldShowCloseButton && (
        <CloseButtonContainer>
          <StyledTextButton
            kind="primary"
            size="large"
            variant="ghost"
            onClick={handleCloseClick}
          >
            <FormattedMessage
              id="pgekZFC5xr72KldqTqsV"
              defaultMessage="Close"
              
            />
          </StyledTextButton>
        </CloseButtonContainer>
      )}
    </Banner>
  )
}
